import React, {
    useState,
    forwardRef,
    useImperativeHandle,
    useEffect,
    ForwardedRef,
} from "react";
import { Card, Checkbox, Input } from "../../../../../shared/custom-ui";

interface NuclearMedicineFormProps {
    defaultValues?: Record<string, any>;
}

const NuclearMedicineForm = forwardRef((props: NuclearMedicineFormProps, ref: ForwardedRef<any>) => {
    const { defaultValues = {} } = props;
    const [nuclearMedicineObj, setNuclearMedicineObj] = useState<Record<string, any>>({});
    const [isPrefilled, setIsPrefilled] = useState(false);

    useEffect(() => {
        if (!isPrefilled && Object.keys(defaultValues).length > 0) {
            setNuclearMedicineObj(prev => ({ ...prev, ...defaultValues }));
            setIsPrefilled(true);
        }
    }, [defaultValues, isPrefilled]);

    useImperativeHandle(ref, () => ({
        getData: () => nuclearMedicineObj,
    }));

    const handleCheckboxChange = (key: string) => {
        setNuclearMedicineObj(prev => {
            if (prev[key]) {
                const { [key]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [key]: true };
        });
    };

    const handleInputChange = (key: string, value: string | null) => {
        setNuclearMedicineObj(prev => {
            if (value) {
                return { ...prev, [key]: value };
            } else {
                const { [key]: _, ...rest } = prev;
                return rest;
            }
        });
    };

    return (
        <Card title="Nuclear Medicine" padding="small">
            <div className="p-4">
                <h2 className="text-lg font-semibold">Bone Scan</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Checkbox
                        label="Whole body"
                        checked={nuclearMedicineObj.whole_body || false}
                        onChange={() => handleCheckboxChange("whole_body")}
                    />
                    <Checkbox
                        label="Three-phase"
                        checked={nuclearMedicineObj.three_phase || false}
                        onChange={() => handleCheckboxChange("three_phase")}
                    />
                    <Checkbox
                        label="Limited area"
                        checked={nuclearMedicineObj.limited_area || false}
                        onChange={() => handleCheckboxChange("limited_area")}
                    />
                    <Checkbox
                        label="Multiple area"
                        checked={nuclearMedicineObj.multiple_area || false}
                        onChange={() => handleCheckboxChange("multiple_area")}
                    />
                    <Checkbox
                        label="With SPECT"
                        checked={nuclearMedicineObj.with_spect || false}
                        onChange={() => handleCheckboxChange("with_spect")}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
                <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                    <span className="font-medium">Specify site</span>
                    <Input
                        placeholder="Specify site"
                        value={nuclearMedicineObj.specify_site || ""}
                        onChange={(e) => handleInputChange("specify_site", e.target.value)}
                    />
                </div>

                <div className="sm:col-span-2">
                    <Checkbox
                        label="X-ray for correlation of positive bone scan findings if indicated"
                        checked={nuclearMedicineObj.xray_correlation || false}
                        onChange={() => handleCheckboxChange("xray_correlation")}
                    />
                </div>
                <Checkbox
                    label="Gastric emptying scan"
                    checked={nuclearMedicineObj.gastric_emptying_scan || false}
                    onChange={() => handleCheckboxChange("gastric_emptying_scan")}
                />
                <Checkbox
                    label="Hepatobiliary (HIDA scan)"
                    checked={nuclearMedicineObj.hida_scan || false}
                    onChange={() => handleCheckboxChange("hida_scan")}
                />

                <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:space-x-4">
                    <Checkbox
                        label="with CCK"
                        checked={nuclearMedicineObj.with_cck || false}
                        onChange={() => handleCheckboxChange("with_cck")}
                    />
                    <Checkbox
                        label="without CCK"
                        checked={nuclearMedicineObj.without_cck || false}
                        onChange={() => handleCheckboxChange("without_cck")}
                    />
                </div>

                <Checkbox
                    label="MUGA scan"
                    checked={nuclearMedicineObj.muga_scan || false}
                    onChange={() => handleCheckboxChange("muga_scan")}
                />
                <Checkbox
                    label="Parathyroid scan"
                    checked={nuclearMedicineObj.parathyroid_scan || false}
                    onChange={() => handleCheckboxChange("parathyroid_scan")}
                />

                <Checkbox
                    label="Renal"
                    checked={nuclearMedicineObj.renal || false}
                    onChange={() => handleCheckboxChange("renal")}
                />
                <div className="flex flex-col sm:flex-row items-start sm:space-x-4">
                    <Checkbox
                        label="with diuretic"
                        checked={nuclearMedicineObj.with_diuretic || false}
                        onChange={() => handleCheckboxChange("with_diuretic")}
                    />
                </div>

                <Checkbox
                    label="Thyroid scan"
                    checked={nuclearMedicineObj.thyroid_scan || false}
                    onChange={() => handleCheckboxChange("thyroid_scan")}
                />
                <div className="flex flex-col sm:flex-row items-start sm:space-x-4">
                    <Checkbox
                        label="with RAIU"
                        checked={nuclearMedicineObj.with_raiu || false}
                        onChange={() => handleCheckboxChange("with_raiu")}
                    />
                </div>

                <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                    <Checkbox
                        label="Other"
                        checked={nuclearMedicineObj.other || false}
                        onChange={() => handleCheckboxChange("other")}
                    />
                    <Input
                        placeholder="Specify other"
                        value={nuclearMedicineObj.specify_other || ""}
                        onChange={(e) => handleInputChange("specify_other", e.target.value)}
                    />
                </div>
            </div>
        </Card>
    );
});

export default NuclearMedicineForm;
